import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import { IErrorMessage } from '../../components/errorMessage/errorMessage.type';
import SearchSelect from '../../components/SearchSelect';
import { COLORS, MQS, SPACINGS } from '../../constants/styles';
import ValidationErrors from '../validationErrors';
import { IGenderItem } from './genderItem';
import { ValidatorRule } from '../../validator/validatorRule.constants';

interface IGenderSelectFieldProps {
    autoFocus?: boolean;
    id?: string;
    intl: InjectedIntl;
    value?: string;
    required?: boolean;
    disabled?: boolean;
    showDobAndGenderCaution?: boolean;
    showCsUrl?: boolean;
    csUrl?: string;
    countryCode: string;
    inputDisabled?: boolean;
    validatorRules?: ValidatorRule[];
}

interface IGenderSelectFieldState {
    selectedGenderItem?: IGenderItem;
    genderItems: IGenderItem[];
}

const GenderSelectWrapper = styled.div<{ disabled?: boolean }>`
    display: flex;
    flex-wrap: wrap;

    & > div:first-child {
        flex: ${({ disabled }) => (disabled ? '2 2 0px' : '9 2 0px')};
    }

    & > :nth-child(2) {
        flex: 9 3 0px;
    }

    @media (max-width: ${MQS.mq2Min}) {
        align-items: left;
        flex-direction: column;
    }
`;

const GenderInfo = styled.p`
    color: ${COLORS.grey7};
    line-height: 1.5;
    font-size: 0.875rem;
    padding: 0 ${SPACINGS.base * 3}px;
    margin: 0;

    a {
        color: ${COLORS.rakutenRed};
        font-weight: 500;

        &,
        &:active &:hover,
        &:focus {
            text-decoration: none;
        }
    }

    @media (max-width: ${MQS.mq2Min}) {
        margin-top: 10px;
        padding: 0;
    }
`;

const DisabledField = styled.div`
    position: relative;
`;

class GenderSelectField extends React.Component<IGenderSelectFieldProps, IGenderSelectFieldState> {
    private readonly ID: string;
    private readonly LABEL_ID: string;

    static getGenderItems(intl: InjectedIntl, countryCode: string): IGenderItem[] {
        const genderItems = [
            {
                code: 'F',
                gender: intl.formatMessage(
                    TranslationHelpers.getMessageDescriptor('label.gender.female'),
                ),
            },
            {
                code: 'M',
                gender: intl.formatMessage(
                    TranslationHelpers.getMessageDescriptor('label.gender.male'),
                ),
            },
            {
                // Note RID only supports 'F' and 'M' values
                code: 'U',
                gender: intl.formatMessage(
                    TranslationHelpers.getMessageDescriptor('label.gender.undefined'),
                ),
            },
        ];

        return genderItems;
    }
    constructor(props: IGenderSelectFieldProps) {
        super(props);
        const { id, value, intl, countryCode } = this.props;

        this.ID = id ? id : 'gender';
        this.LABEL_ID = 'label.gender';
        const genderItems = GenderSelectField.getGenderItems(intl, countryCode);

        this.state = {
            selectedGenderItem: genderItems.find((genderItem) => value === genderItem.code),
            genderItems,
        };

        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);
    }

    static getDerivedStateFromProps(
        nextProps: IGenderSelectFieldProps,
        prevState: IGenderSelectFieldState,
    ) {
        const { intl, value, countryCode } = nextProps;
        const genderItems = GenderSelectField.getGenderItems(intl, countryCode);

        const state = {
            selectedGenderItem: genderItems.find((genderItem) => value === genderItem.code),
            genderItems,
        };

        return state;
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(value: string): IErrorMessage {
        const { required } = this.props;

        const errorMessage: IErrorMessage = '';

        if (!value && required) {
            return ValidationErrors.fieldRequiredMessageProps(this.LABEL_ID);
        }

        return errorMessage;
    }

    private renderInputField({
        form: { touched, errors, setFieldValue, setFieldTouched },
    }: FieldProps): ReactNode {
        const {
            disabled,
            intl,
            required,
            csUrl,
            showCsUrl = true,
            showDobAndGenderCaution = false,
            inputDisabled,
        } = this.props;
        const { selectedGenderItem, genderItems } = this.state;
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const onGenderSelect = (genderItem: IGenderItem) => {
            this.setState({ selectedGenderItem: genderItem });
            setFieldValue(this.ID, genderItem ? genderItem.code : '');
        };
        const customerSupportText = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor('customer_support'),
        );

        return (
            <GenderSelectWrapper disabled={disabled}>
                {disabled ? (
                    <DisabledField>
                        <label className={'form-label'} id={this.LABEL_ID}>
                            {TranslationHelpers.getFormattedMessage(this.LABEL_ID)} *
                        </label>
                        <label style={{ margin: '0 0.6rem' }} aria-describedby={this.LABEL_ID}>
                            {selectedGenderItem?.gender}
                        </label>
                    </DisabledField>
                ) : (
                    <SearchSelect
                        autoComplete="sex"
                        id={this.ID}
                        selectedItem={selectedGenderItem}
                        items={genderItems}
                        label={TranslationHelpers.getMessageDescriptor(this.LABEL_ID)}
                        error={errorMessage}
                        searchKey={'gender'}
                        required={required}
                        onSelect={onGenderSelect}
                        setTouched={setFieldTouched}
                        disabled={disabled}
                        inputDisabled={inputDisabled}
                    />
                )}
                {showDobAndGenderCaution && (
                    <GenderInfo>
                        {TranslationHelpers.getFormattedMessage(
                            showCsUrl
                                ? 'gender.disabled_message'
                                : 'gender.disabled_message_no_csurl',
                            {
                                cs: <a href={csUrl}>{customerSupportText}</a>,
                            },
                        )}
                    </GenderInfo>
                )}
            </GenderSelectWrapper>
        );
    }
}

export default injectIntl(GenderSelectField);
